import { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  EmptyState,
  ErrorNote,
  PageHeader,
} from "@/components/ui";
import type { components } from "@/generated/openapi-types";
import {
  errorMessage,
  useIntegrationDestinations,
  useIntegrations,
  useInvalidating,
} from "@/lib/api";
import { cn } from "@/lib/cn";

type Integration = components["schemas"]["Integration"];

/**
 * What the assistant may do in this customer's own systems.
 *
 * Connecting sends them to the provider's consent screen; the token that
 * comes back is held by AWS against this organization and never by us.
 * Each tool is a separate tick, because "read my contacts" and "write to
 * my CRM" are not the same decision.
 */
export function IntegrationsScreen() {
  const integrations = useIntegrations();
  const [error, setError] = useState<string | null>(null);

  const completeConsent = useInvalidating(
    (api, sessionId: string) =>
      api.POST("/integrations/consent", { body: { sessionId } }),
    [["integrations"]],
  );
  const connect = useInvalidating(
    (api, provider: string) =>
      api.POST("/integrations/{provider}/connect", {
        params: { path: { provider } },
        body: { returnUrl: `${window.location.origin}/integrations` },
      }),
    [["integrations"]],
  );
  const setTools = useInvalidating(
    (api, args: { provider: string; tools: string[] }) =>
      api.PUT("/integrations/{provider}/tools", {
        params: { path: { provider: args.provider } },
        body: { tools: args.tools },
      }),
    [["integrations"]],
  );
  const setDestination = useInvalidating(
    (api, args: { provider: string; id: string }) =>
      api.PUT("/integrations/{provider}/destination", {
        params: { path: { provider: args.provider } },
        body: { id: args.id },
      }),
    [["integrations"]],
  );
  const disconnect = useInvalidating(
    (api, provider: string) =>
      api.DELETE("/integrations/{provider}", {
        params: { path: { provider } },
      }),
    [["integrations"]],
  );

  // Coming back from the provider's consent screen.
  //
  // AgentCore will not fetch the token until we confirm the browser that
  // returned is the same tenant that started the flow — so this call is
  // what turns a completed consent into a usable connection. Without it
  // the customer lands on a page that still says "Not connected" while
  // their authorization sits unfinished.
  //
  // The id is consumed once and stripped from the URL: a session is
  // single-use and ten minutes long, so leaving it in the address bar
  // only invites a reload to fail against a session already spent.
  const consumed = useRef(false);
  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get(
      "session_id",
    );
    if (!sessionId || consumed.current) return;
    consumed.current = true;
    window.history.replaceState(null, "", window.location.pathname);
    completeConsent
      .mutateAsync(sessionId)
      .catch((err: unknown) =>
        setError(errorMessage(err, "Could not finish connecting.")),
      );
  }, [completeConsent]);

  const start = async (provider: string) => {
    setError(null);
    try {
      const result = await connect.mutateAsync(provider);
      // A provider that needs consent hands back a URL to send them to.
      if (result.authorizationUrl)
        window.location.assign(result.authorizationUrl);
    } catch (err) {
      setError(errorMessage(err, "Could not start the connection."));
    }
  };

  const items = integrations.data?.items ?? [];
  return (
    <>
      <PageHeader
        title="Integrations"
        subtitle="Let the assistant act in your own systems: push a lead to your CRM, look a contact up, book a meeting."
      />
      <ErrorNote message={error} />
      <div className="flex flex-col gap-4">
        {items.length === 0 ? (
          <Card>
            <EmptyState title="No integrations available yet." />
          </Card>
        ) : null}
        {items.map((it) => {
          const connected = it.status === "connected";
          const enabled = it.tools.filter((t) => t.enabled).map((t) => t.name);
          return (
            <Card key={it.provider} className="flex flex-col gap-4">
              <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="text-[16px] font-bold tracking-[-0.01em]">
                      {it.label}
                    </h2>
                    <span
                      className={cn(
                        "rounded-full px-2 py-0.5 text-[11px] font-semibold",
                        connected
                          ? "bg-success/10 text-success"
                          : it.status === "error"
                            ? "bg-danger-soft text-danger"
                            : "bg-surface-2 text-muted",
                      )}
                    >
                      {connected
                        ? "Connected"
                        : it.status === "error"
                          ? "Error"
                          : "Not connected"}
                    </span>
                  </div>
                  <p className="mt-1 max-w-[60ch] text-[13px] text-muted">
                    {it.description}
                  </p>
                  {it.accountLabel ? (
                    <p className="mt-1 text-[12px] text-muted">
                      Account: {it.accountLabel}
                    </p>
                  ) : null}
                  {it.error ? (
                    <p className="mt-1 text-[12px] text-danger">{it.error}</p>
                  ) : null}
                </div>
                {connected ? (
                  <Button
                    variant="danger"
                    onClick={() => disconnect.mutate(it.provider)}
                  >
                    Disconnect
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    disabled={connect.isPending}
                    onClick={() => void start(it.provider)}
                  >
                    {connect.isPending ? "Connecting…" : "Connect"}
                  </Button>
                )}
              </header>

              {it.destination && connected ? (
                <DestinationPicker
                  provider={it.provider}
                  chosen={it.destination}
                  onChoose={(id) =>
                    setDestination.mutate({ provider: it.provider, id })
                  }
                  saving={setDestination.isPending}
                />
              ) : null}

              <div className="border-t border-border pt-3">
                <p className="eyebrow mb-2">What the assistant may do</p>
                <ul className="flex flex-col gap-2">
                  {it.tools.map((tool) => (
                    <li key={tool.name} className="flex items-start gap-3">
                      <input
                        id={`${it.provider}-${tool.name}`}
                        type="checkbox"
                        className="mt-1 h-4 w-4 accent-[rgb(var(--color-accent))]"
                        checked={tool.enabled}
                        disabled={!connected || setTools.isPending}
                        onChange={(e) =>
                          setTools.mutate({
                            provider: it.provider,
                            tools: e.target.checked
                              ? [...enabled, tool.name]
                              : enabled.filter((n) => n !== tool.name),
                          })
                        }
                      />
                      <label
                        htmlFor={`${it.provider}-${tool.name}`}
                        className="min-w-0"
                      >
                        <span className="block text-[13px] font-medium">
                          {tool.label}
                        </span>
                        <span className="block text-[12px] text-muted">
                          {tool.description}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
                {!connected ? (
                  <p className="mt-2 text-[12px] text-muted">
                    Connect the integration to choose what the assistant may do.
                  </p>
                ) : null}
              </div>
            </Card>
          );
        })}
      </div>
    </>
  );
}

/**
 * Which Slack channel this company's leads go to.
 *
 * The list is fetched from Slack when the section renders, not cached: a
 * channel made a minute ago should be in it, and a stale list is how
 * someone picks a channel that has since been archived.
 */
function DestinationPicker({
  provider,
  chosen,
  onChoose,
  saving,
}: {
  provider: string;
  chosen: NonNullable<Integration["destination"]>;
  onChoose: (id: string) => void;
  saving: boolean;
}) {
  const destinations = useIntegrationDestinations(provider);
  const options = destinations.data?.items ?? [];

  return (
    <div className="border-t border-border pt-3">
      <p className="eyebrow mb-2">Where leads go</p>
      {destinations.isLoading ? (
        <p className="text-[13px] text-muted">Loading your channels…</p>
      ) : destinations.isError ? (
        <p className="text-[13px] text-danger">
          Could not read your channels. Try reconnecting.
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <select
            className="rounded-md border border-border bg-surface px-2 py-1.5 text-[13px]"
            value={chosen.id ?? ""}
            disabled={saving}
            onChange={(e) => {
              if (e.target.value) onChoose(e.target.value);
            }}
          >
            <option value="" disabled>
              Choose a channel…
            </option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
          <span className="text-[12px] text-muted">
            {chosen.chosen
              ? "Every lead is posted here the moment it is captured."
              : "Pick a channel and every lead will be posted there."}
          </span>
        </div>
      )}
    </div>
  );
}
